import { OutlineButton } from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import { Box, styled } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { MobileRetailerConnectBenefits } from "./MobileRetailerConnectBenefits";
import { MobileRetailerConnectLogin } from "./MobileRetailerConnectLogin";
import LeavingConfirm from "./LeavingConfirm";
import { ResponsiveDialog } from "@/components/StoreComponents/StoreDialogs/ResponsiveDialog";
import { Retailer } from "@/redux/types";

enum PopupStatus {
  WELCOME = "welcome",
  CONNECT = "connect",
  LEAVING = "leaving",
}

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.palette.primary.main,
}));

const EditConnectPopup = ({
  showEdit,
  closeEdit,
  onChange,
  connectReward,
  onSaveCredentials,
  currentUsername,
  currentPassword,
  retailer,
}: {
  showEdit: boolean;
  closeEdit: () => void;
  connectReward: number;
  onChange?: (event: {
    username: string;
    password: string;
    edited: boolean;
  }) => void;
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  currentUsername: string;
  currentPassword: string;
  retailer?: Retailer;
}) => {
  const [status, setStatus] = useState<PopupStatus>(PopupStatus.WELCOME);
  const [previousStatus, setPreviousStatus] = useState<PopupStatus>(
    PopupStatus.WELCOME
  );

  const onCloseEdit = () => {
    setStatus(PopupStatus.WELCOME);
    closeEdit();
  };

  const poweredBy = "Brandclub";

  const storeName = useAppSelector(
    (state) => state.appConfig?.domainConfig?.storeName
  );

  const poweredByText = storeName?.includes("Brandclub") ? (
    <span>
      <span style={{ fontWeight: "600" }}>Brandclub</span>
      <span style={{ fontWeight: "400" }}>
        {" "}
        uses your retailer account to place your orders
      </span>
    </span>
  ) : (
    <span>
      <span style={{ fontWeight: "600" }}>{storeName}</span>
      <span style={{ fontWeight: "400" }}> uses </span>
      <span style={{ fontWeight: "600" }}>{poweredBy}</span>
      <span style={{ fontWeight: "400" }}>
        {" "}
        to process your orders directly under your retailer account
      </span>
    </span>
  );

  const getDialogBody = () => {
    switch (status) {
      case PopupStatus.WELCOME:
        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={(theme) => ({
                fontSize: 18,
                [theme.breakpoints.down("xs")]: {
                  fontSize: 16,
                },
                fontWeight: 500,
                marginBottom: "16px",
              })}
            >
              {poweredByText}
            </Box>
            <MobileRetailerConnectBenefits rewardAmount={connectReward} />
            <OutlineButton
              disableMaxWidth
              sx={{
                marginTop: "16px",
                width: "100%",
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                alignItems: "center",
                maxWidth: 450,
              }}
              onClick={() => {
                setStatus(PopupStatus.CONNECT);
              }}
            >
              Continue
            </OutlineButton>
          </Box>
        );
      case PopupStatus.CONNECT:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <MobileRetailerConnectLogin
              closeEdit={onCloseEdit}
              onSaveCredentials={onSaveCredentials}
              onChange={onChange}
              currentUsername={currentUsername}
              currentPassword={currentPassword}
              retailer={retailer}
            />
          </Box>
        );
      case PopupStatus.LEAVING:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <LeavingConfirm
              closeEdit={onCloseEdit}
              goBack={() => {
                setStatus(previousStatus);
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog
      open={showEdit}
      onClose={
        status === PopupStatus.LEAVING
          ? undefined
          : () => {
              setPreviousStatus(status);
              setStatus(PopupStatus.LEAVING);
            }
      }
    >
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            height: 600,
            width: 700,
          },
          overflowY: "auto",
          maxWidth: "100%",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",
              padding: "24px 24px",
              flexDirection: "column",
            }}
            key={status}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            {getDialogBody()}
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};

export default EditConnectPopup;
